import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FaHome,
  FaChartLine,
  FaBuilding,
  FaBox,
  FaCogs,
  FaBell,
  FaFileAlt,
  FaShieldAlt,
  FaKey,
  FaBars,
  FaUsers,
  FaUserShield,
  FaCloud,
} from "react-icons/fa";

import usePermissions from "../hooks/usePermissions";
import carbelimLogo from "../assets/carbelim-logo.png";
import { BRANDING_CHANGED_EVENT, getStoredBranding, loadCompanyBranding } from "../utils/companyBranding";

/* ─────────────────────────────────────────
   MENU DEFINITION
───────────────────────────────────────── */
const MENU = [
  {
    section: "Command Center",
    items: [
      {
        label: "Dashboard",
        icon: FaHome,
        path: "/dashboard",
        module: "dashboard",
      },
      {
        label: "Devices",
        icon: FaCogs,
        path: "/dashboard/devices",
        module: "devices",
      },
      {
        label: "Device Health",
        icon: FaChartLine,
        path: "/dashboard/device-health",
        module: "devices",
      },
      {
        label: "CO2 Devices",
        icon: FaCloud,
        path: "/dashboard/co2-devices",
        module: "devices",
      },
      {
        label: "Alerts",
        icon: FaBell,
        path: "/dashboard/alerts",
        module: "alerts",
      },
      {
        label: "Maintenance",
        icon: FaCogs,
        path: "/dashboard/maintenance",
        module: "maintenance",
      },
      {
        label: "Reports",
        icon: FaFileAlt,
        path: "/dashboard/reports",
        module: "reports",
      },
      {
        label: "ESG",
        icon: FaChartLine,
        path: "/dashboard/esg-reports",
        module: "esg",
      },
    ],
  },
  {
    section: "Workspace",
    items: [
      {
        label: "Products",
        icon: FaBox,
        path: "/dashboard/products",
        module: "products",
      },
      {
        label: "Companies",
        icon: FaBuilding,
        path: "/dashboard/companies",
        module: "companies",
      },
      {
        label: "Users",
        icon: FaUsers,
        path: "/dashboard/users",
        module: "users",
      },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        label: "Admins",
        icon: FaUserShield,
        path: "/dashboard/admins",
        module: "users",
        allowedRoles: ["super_admin"],
      },
      {
        label: "Roles",
        icon: FaShieldAlt,
        path: "/dashboard/roles",
        module: "roles",
      },
      {
        label: "API Keys",
        icon: FaKey,
        path: "/dashboard/api-keys",
        module: "api_keys",
      },
      {
        label: "API Manager",
        icon: FaFileAlt,
        path: "/dashboard/api-manager/dashboard",
        module: "dashboard",
      },
    ],
  },
];

/* ─────────────────────────────────────────
   LOGO ICON
───────────────────────────────────────── */
const LogoIcon = ({ branding }) => (
  <img
    src={branding?.logo_data || carbelimLogo}
    alt={branding?.company_name || "Carbelim"}
    className="w-9 h-9 rounded-lg object-contain flex-shrink-0 bg-emerald-50 p-1.5 ring-1 ring-emerald-100"
  />
);

/* ─────────────────────────────────────────
   TOGGLE BUTTON
───────────────────────────────────────── */
const ToggleBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    className="
      w-[30px] h-[30px]
      flex items-center justify-center
      rounded-lg
      bg-slate-50
      border border-slate-200
      text-slate-500
      hover:bg-emerald-50
      hover:text-emerald-700
      transition-all duration-200
      flex-shrink-0
    "
  >
    <FaBars size={12} />
  </button>
);

/* ─────────────────────────────────────────
   SIDEBAR
───────────────────────────────────────── */
const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [branding, setBranding] = useState(getStoredBranding);

  const { canView } = usePermissions();
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const currentRole = String(currentUser.role || "").toLowerCase().replace(/\s+/g, "_");
  const [hasCo2Access, setHasCo2Access] = useState(currentRole === "super_admin");

  useEffect(() => {
    loadCompanyBranding().then(setBranding).catch(() => {});
    const refreshBranding = () => setBranding(getStoredBranding());
    window.addEventListener(BRANDING_CHANGED_EVENT, refreshBranding);
    return () => window.removeEventListener(BRANDING_CHANGED_EVENT, refreshBranding);
  }, []);

  useEffect(() => {
    if (currentRole === "super_admin") return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch("/api/co2/access", { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => response.ok ? response.json() : { access: false })
      .then((data) => setHasCo2Access(!!data.access))
      .catch(() => setHasCo2Access(false));
  }, [currentRole]);

  return (
    <aside
      className={`
        h-screen
        cb-sidebar
        border-r border-slate-200/80
        flex flex-col
        flex-shrink-0
        overflow-hidden
        transition-all duration-300
        shadow-2xl shadow-slate-900/5
        ${collapsed ? "w-[72px]" : "w-[258px]"}
      `}
    >
      {/* ───────────────── Header ───────────────── */}
      <div
        className={`
          flex items-center flex-shrink-0
          border-b border-slate-200/80
          ${collapsed
            ? "justify-center py-[18px] px-0"
            : "justify-between px-4 py-4"}
        `}
      >
        {collapsed ? (
          <ToggleBtn onClick={() => setCollapsed(false)} />
        ) : (
          <>
            <div className="flex items-center gap-2.5">
              <LogoIcon branding={branding} />

              <div className="flex flex-col leading-tight">
                <span className="text-[16px] font-bold text-slate-950">
                  {branding?.company_name || "Carbelim"}
                </span>

                <span className="text-[10px] text-emerald-700 font-semibold uppercase">
                  IoT Platform
                </span>
              </div>
            </div>

            <ToggleBtn onClick={() => setCollapsed(true)} />
          </>
        )}
      </div>

      {/* ───────────────── Navigation ───────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {MENU.map((group) => {
          const visibleItems = group.items.filter((item) => {
            const roleAllowed = !item.allowedRoles || item.allowedRoles.includes(currentRole);
            const co2Allowed = item.path !== "/dashboard/co2-devices" || hasCo2Access;
            // CO2 access is granted by an explicit per-user allotment, not by
            // the broad Devices permission used for the rest of the platform.
            if (item.path === "/dashboard/co2-devices") return co2Allowed;
            return roleAllowed && canView(item.module);
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={group.section} className="mb-3">
              {/* Section Label */}
              {collapsed ? (
                <div className="h-3" />
              ) : (
                <p
                  className="
                    text-[10px]
                    font-semibold
                    text-slate-400
                    uppercase
                    tracking-[0.7px]
                    px-2.5
                    pt-2
                    pb-1
                  "
                >
                  {group.section}
                </p>
              )}

              <div className="space-y-1">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const itemContent = (isActive = false) => (
                    <div
                      title={collapsed ? item.label : undefined}
                      className={`
                        relative
                        flex items-center
                        gap-2.5
                        rounded-lg
                        cursor-pointer
                        transition-all duration-200
                        select-none

                        ${
                          collapsed
                            ? "justify-center px-0 py-2.5"
                            : "px-2.5 py-[8px]"
                        }

                        ${
                          isActive
                            ? "cb-sidebar-item-active"
                            : "cb-sidebar-item"
                        }
                      `}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-[18%] h-[64%] w-[3px] bg-emerald-500 rounded-r-full" />
                      )}

                      <div
                        className={`
                          w-[32px]
                          h-[32px]
                          flex items-center justify-center
                          rounded-[9px]
                          flex-shrink-0
                          transition-all duration-200

                          ${
                            isActive
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-100 text-slate-500"
                          }
                        `}
                      >
                        <Icon size={13} />
                      </div>

                      {!collapsed && (
                        <span
                          className={`
                            text-[13px]
                            whitespace-nowrap

                            ${
                              isActive
                                ? "font-semibold"
                                : "font-medium"
                            }
                          `}
                        >
                          {item.label}
                        </span>
                      )}
                    </div>
                  );

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/dashboard"}
                      className="block no-underline"
                    >
                      {({ isActive }) => itemContent(isActive)}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
{/* ───────────────── Footer ───────────────── */}
      {!collapsed && (
        <div
          className="
            px-4 py-3
            border-t border-slate-200/80
            text-[10px]
            text-slate-400
            font-medium
            tracking-wide
            flex-shrink-0
          "
        >
          Carbelim Sustainability Platform
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
